/* ==========================================================================
   KS — Mega menu panel: live search
   Loaded by sections/mega-menu-panel.liquid.

   Split out of that section's inline <script> for the same reason the CSS was:
   changing behaviour shouldn't mean rewriting a 33KB Liquid file. Nothing here
   needs Liquid — the search URL comes off the panel's data-search-url attribute
   and everything else is found by class/attribute inside the panel.

   SEARCH SOURCE: results come from /search?...&view=ks-rows — the SAME engine and
   params the search page uses (templates/search.ks-rows.liquid renders the rows).
   This deliberately replaced /search/suggest: predictive search is a different
   engine (prefix matching on a limited set of fields) to full storefront search,
   so the panel and the search page returned different products for the same term.
   If you ever point this back at the suggest endpoint, expect them to diverge again.

   The rows themselves are snippets/ks-cart-style-row output, which is the cart
   drawer's suggestion markup — so the size-pill and ADD handlers in
   snippets/cart-drawer.liquid (document-level delegates) drive them with no JS here.

   FOUR DISPLAY STATES, all sharing the same centred area:
     rows    — list visible, prompt and spinner hidden
     empty   — list visible holding the "no results" message from the template
     loading — spinner only, while a request is in flight
     prompt  — prompt only: field focused with nothing typed

   VIEW ALL RESULTS is visible through all four — it only leaves the screen when
   the search area closes. When there's nothing to view (no results, or a request
   still in flight) it greys out via .is-disabled rather than disappearing, so the
   row-4 footer never collapses and reflows the layout mid-typing. The label never
   changes.

   ONE REQUEST PER SEARCH. There used to be a second fetch against
   templates/search.count.liquid purely to put a number in the button's label. With
   the label fixed there's nothing left for it to decide — whether anything is
   worth viewing is already answered by the rows themselves — so it's gone, and
   every keystroke now costs one request instead of two. That template is no longer
   referenced by anything.

   KS — MOBILE: ONLY A GENUINE TAP CLOSES THE SEARCH ON BLUR, NOT A KEYBOARD
   DISMISS. The focusout listener below used to be desktop-only outright — that
   fixed the keyboard's dismiss control (the checkmark/chevron in its accessory
   bar) collapsing the search, since dismissing the keyboard blurs the input with
   nothing else ever taking focus. But it over-corrected: tapping somewhere else in
   the panel on mobile (e.g. above the field, near the SEARCH heading) is a
   genuine "move on from search" gesture and SHOULD still close it, and the
   blanket desktop-only guard silently ate that case too.

   The fix tells the two apart by whether a real pointerdown ever touched the
   page. A keyboard dismiss is OS-level UI — it never fires a pointerdown on any
   element in the document, so lastPointerTarget stays wherever it was (inside the
   search UI, from focusing the field, or null). A genuine tap elsewhere always
   fires pointerdown on that element first, updating lastPointerTarget to
   something outside searchWrap/resultsEl/viewAllEl. On mobile, the collapse only
   runs when that's true; on desktop it always runs, same as before — desktop's
   own "click away = leave search" expectation doesn't need this extra check. */

(() => {
  const DEBOUNCE_MS = 250;
  /* Used only if --ks-panel-in-delay can't be read off the panel. */
  const PROMPT_DELAY_FALLBACK_MS = 60;
  const VIEW_ALL_LABEL = 'VIEW ALL RESULTS';

  /* Same spinner markup and classes the search page uses, so it reads as one
     component; the stroke is repainted white in assets/ks-panel-search.css. Built
     here rather than in the section so spinner changes don't mean rewriting Liquid. */
  const SPINNER_HTML =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
    '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';

  /* Tracks the most recent real pointer interaction anywhere on the page — not
     scoped to the panel, since "tapped elsewhere" specifically means outside it.
     Capture phase so it's recorded before anything else (including this panel's
     own handlers) can stop propagation. See the header note (MOBILE: ONLY A
     GENUINE TAP CLOSES...) for why this exists. */
  let lastPointerTarget = null;
  document.addEventListener('pointerdown', (e) => {
    lastPointerTarget = e.target;
  }, true);

  /* The prompt is shown from here rather than by the CSS arrival animation, so it
     needs the same head start the results and VIEW ALL RESULTS get. Reading the
     variable instead of hardcoding a copy means retiming the fade is a CSS-only
     change — these two used to be separate numbers and drifted apart every time.

     Read at call time, not at init: the stylesheet is loaded with
     media=print/onload, so the variable may not be applied yet when the panel is
     first bound. By the time anyone focuses the field it certainly is. */
  function promptDelayMs(el) {
    const raw = getComputedStyle(el).getPropertyValue('--ks-panel-in-delay').trim();
    if (!raw) return PROMPT_DELAY_FALLBACK_MS;
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return PROMPT_DELAY_FALLBACK_MS;
    return raw.endsWith('ms') ? value : value * 1000;
  }

  /* Capitalises the first letter as the person types, matching the search page's
     field (see the input handler in sections/main-search.liquid). The caret is put
     back where it was, since assigning to input.value moves it to the end — which
     would make editing anywhere but the end of the term impossible. Search itself
     is case-insensitive, so this is presentation only. */
  function capitaliseFirst(field) {
    const raw = field.value;
    if (!raw.length) return;

    const capitalised = raw.charAt(0).toUpperCase() + raw.slice(1);
    if (capitalised === raw) return;

    const selStart = field.selectionStart;
    const selEnd = field.selectionEnd;
    field.value = capitalised;
    if (selStart !== null && selEnd !== null) field.setSelectionRange(selStart, selEnd);
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Only used if the request itself fails — the real empty state is rendered by
     templates/search.ks-rows.liquid from the theme's locale string. Worded the same. */
  function emptyHtml(term) {
    return '<p class="mega-menu-panel__search-empty">No results found for \u201C' + escapeHtml(term) +
      '\u201D<br>Check the spelling or use a different word or phrase.</p>';
  }

  function initPanel(panel) {
    if (!panel || panel.dataset.ksSearchBound === 'true') return;

    const input = panel.querySelector('.mega-menu-panel__search-input');
    const searchWrap = panel.querySelector('.mega-menu-panel__search');
    const searchForm = panel.querySelector('.search--panel');
    const resultsEl = panel.querySelector('[data-search-results]');
    const resultsList = resultsEl ? resultsEl.querySelector('[data-search-results-list]') : null;
    const promptEl = resultsEl ? resultsEl.querySelector('[data-search-prompt]') : null;
    /* view-all is a sibling of the results (own grid row), not a child of it. */
    const viewAllEl = panel.querySelector('[data-search-view-all]');
    const viewAllText = viewAllEl ? viewAllEl.querySelector('[data-search-view-all-text]') : null;

    if (!input || !resultsEl || !resultsList || !viewAllEl || !viewAllText) return;

    panel.dataset.ksSearchBound = 'true';
    viewAllText.textContent = VIEW_ALL_LABEL;

    /* Spinner is a sibling of the list and the prompt, so margin:auto centres it in
       the same spot both messages land in. */
    let spinnerEl = resultsEl.querySelector('[data-search-spinner]');
    if (!spinnerEl) {
      spinnerEl = document.createElement('div');
      spinnerEl.className = 'mega-menu-panel__search-spinner loading-overlay__spinner';
      spinnerEl.setAttribute('data-search-spinner', '');
      spinnerEl.hidden = true;
      spinnerEl.innerHTML = SPINNER_HTML;
      resultsEl.appendChild(spinnerEl);
    }

    const searchUrl = panel.dataset.searchUrl || '/search';
    const mqDesktop = window.matchMedia('(min-width: 990px)');

    let fetchController = null;
    let expanded = false;
    let searchTimer = null;
    let promptTimer = null;

    /* Set while navigating to the full search page. See the guard in the
       MutationObserver at the bottom for why this exists. */
    let submitting = false;

    /* The term the current rows are for — what VIEW ALL RESULTS links to. */
    let lastTerm = '';

    function buildUrl(term, view) {
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      if (view) url.searchParams.set('view', view);
      return url;
    }

    function expandSearch() {
      if (expanded || !mqDesktop.matches) return;
      panel.classList.add('mega-menu-panel--search-active');
      expanded = true;
    }

    function collapseSearch() {
      if (!expanded) return;
      panel.classList.remove('mega-menu-panel--search-active');
      expanded = false;
    }

    function openArea() {
      resultsEl.hidden = false;
      panel.classList.add('mega-menu-panel--results-open');
      input.setAttribute('aria-expanded', 'true');
    }

    function hidePrompt() {
      clearTimeout(promptTimer);
      if (!promptEl) return;
      promptEl.classList.remove('is-visible');
      promptEl.hidden = true;
    }

    function showSpinner() {
      hidePrompt();
      resultsList.hidden = true;
      spinnerEl.hidden = false;
      openArea();
      syncViewAll();
    }

    function hideSpinner() {
      spinnerEl.hidden = true;
    }

    /* Called whenever anything that affects the button changes. The button tracks
       the search area's visibility; only its ENABLED state depends on there being
       rows on screen to view. */
    function syncViewAll() {
      viewAllEl.hidden = resultsEl.hidden;
      if (lastTerm) viewAllEl.href = buildUrl(lastTerm).toString();

      const rowsVisible = !resultsEl.hidden && !resultsList.hidden && spinnerEl.hidden;
      const enabled = rowsVisible && !!resultsList.querySelector('.cart-item');

      if (enabled) {
        viewAllEl.classList.remove('is-disabled');
        viewAllEl.removeAttribute('aria-disabled');
        viewAllEl.removeAttribute('tabindex');
      } else {
        viewAllEl.classList.add('is-disabled');
        viewAllEl.setAttribute('aria-disabled', 'true');
        viewAllEl.setAttribute('tabindex', '-1');
      }
    }

    /* Fade needs a frame at opacity 0 with the element displayed, so clear the
       hidden attribute first and add the class on the next frame. */
    function schedulePrompt() {
      if (!promptEl) return;
      clearTimeout(promptTimer);
      clearTimeout(searchTimer);
      if (fetchController) fetchController.abort();

      lastTerm = '';

      resultsList.innerHTML = '';
      resultsList.hidden = true;
      hideSpinner();
      promptEl.hidden = true;
      promptEl.classList.remove('is-visible');
      openArea();
      syncViewAll();

      promptTimer = setTimeout(() => {
        promptEl.hidden = false;
        requestAnimationFrame(() => promptEl.classList.add('is-visible'));
      }, promptDelayMs(panel));
    }

    function showResults() {
      hidePrompt();
      hideSpinner();
      resultsList.hidden = false;
      openArea();
      syncViewAll();
    }

    function hideResults() {
      hidePrompt();
      hideSpinner();
      resultsEl.hidden = true;
      resultsList.hidden = false;
      panel.classList.remove('mega-menu-panel--results-open');
      input.setAttribute('aria-expanded', 'false');
      syncViewAll();
    }

    /* Rows come back as bare HTML from templates/search.ks-rows.liquid ({% layout none %}),
       so there's no section wrapper to unwrap — inject the response as-is. */
    function fetchSuggestions(term) {
      if (fetchController) fetchController.abort();
      fetchController = new AbortController();

      showSpinner();

      fetch(buildUrl(term, 'ks-rows').toString(), { signal: fetchController.signal })
        .then((res) => res.text())
        .then((html) => {
          const markup = html.trim();
          resultsList.innerHTML = markup.length ? markup : emptyHtml(term);
          lastTerm = term;
          showResults();
        })
        .catch((err) => {
          /* An abort means a newer keystroke already took over — leave its spinner up. */
          if (err.name !== 'AbortError') {
            resultsList.innerHTML = emptyHtml(term);
            lastTerm = term;
            showResults();
          }
        });
    }

    /* KEEPS THE FIELD FOCUSED WHILE USING A ROW. Picking a size or hitting ADD
       has to leave the search open, but the close logic below decides by looking
       at document.activeElement — and a size pill is an <li>, which can't take
       focus. Clicking one blurred the field, focus fell to <body>, the "still
       inside the search area" test failed, and the whole panel collapsed
       mid-interaction.

       Cancelling mousedown's default stops focus moving in the first place, so
       the field never blurs and there's nothing to recover from. The click event
       still fires, so the size-pill and ADD delegates in cart-drawer.liquid run
       exactly as before. Real links are left alone — they navigate away, and
       they should be focusable in their own right.

       SCOPED TO .cart-item: this only guards clicks inside an actual product row.
       A click in the empty space around/between rows — or across the whole area
       when there's nothing in it — is deliberately left alone here so the click
       listener below can close the search instead. See the header note (EMPTY-GRID
       CLICK CLOSES SEARCH). */
    resultsEl.addEventListener('mousedown', (e) => {
      if (e.target.closest('a[href]')) return;
      if (e.target.closest('.cart-item')) e.preventDefault();
    });

    /* KS — closes the search when the click landed OUTSIDE any product row: empty
       grid space between rows, or the whole area when it's holding the "no
       results" message or nothing at all. A real product row is left alone — a
       link click navigates away on its own, and anything else inside a row (size
       pill, ADD) is already handled by its own delegate and shouldn't close
       search anyway. Works identically on mobile and desktop, since it doesn't
       depend on the blur/focusout chain below at all. */
    resultsEl.addEventListener('click', (e) => {
      if (e.target.closest('.cart-item')) return;
      hideResults();
      collapseSearch();
      if (searchWrap) searchWrap.classList.remove('is-focused');
      input.blur();
    });

    /* pointer-events:none already blocks mouse clicks on the greyed state; this
       covers keyboard activation and anything that synthesises a click. */
    viewAllEl.addEventListener('click', (e) => {
      if (viewAllEl.classList.contains('is-disabled')) e.preventDefault();
    });

    /* NAVIGATION TO THE FULL SEARCH PAGE is done here rather than by letting the
       form submit itself, because relying on the browser's own serialisation put
       the term at the mercy of everything else that fires on submit.

       The section used to close the panel on submit, which flips aria-hidden,
       which lands in the MutationObserver below — and that resets the field.
       Observer callbacks are MICROTASKS, so they drain before the browser builds
       the form's entry list: `q` was already empty by the time it was serialised,
       and Enter arrived at /search?q= with a blank results page. That close() is
       gone now, and reading input.value here sidesteps the ordering question
       entirely — nothing that runs afterwards can change what was sent.

       It also means Enter, the inline FIND and the desktop FIND all take exactly
       one code path, and the URL is byte-identical to the VIEW ALL RESULTS href,
       since both come from buildUrl(). An empty field submits nothing at all
       rather than navigating to a blank search page. */
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        e.preventDefault();

        const term = input.value.trim();
        if (!term.length) return;

        submitting = true;
        window.location.assign(buildUrl(term).toString());
      });
    }

    /* A cached page restored with the back button keeps this closure alive, so
       clear the flag or the field would never reset again. */
    window.addEventListener('pageshow', () => {
      submitting = false;
    });

    input.addEventListener('focus', () => {
      searchWrap && searchWrap.classList.add('is-focused');
      expandSearch();
      if (input.value.trim().length) {
        showResults();
      } else {
        schedulePrompt();
      }
    });

    input.addEventListener('input', () => {
      capitaliseFirst(input);

      const term = input.value.trim();

      clearTimeout(searchTimer);

      if (!term.length) {
        schedulePrompt();
        return;
      }

      hidePrompt();

      searchTimer = setTimeout(() => {
        fetchSuggestions(term);
      }, DEBOUNCE_MS);
    });

    document.addEventListener('focusout', (e) => {
      if (!panel.contains(e.target)) return;
      if (!searchWrap) return;
      if (
        !searchWrap.contains(e.target) &&
        !resultsEl.contains(e.target) &&
        !viewAllEl.contains(e.target)
      ) return;

      /* KS — mobile only: require a genuine tap elsewhere before treating this as
         "leave search". See the header note (MOBILE: ONLY A GENUINE TAP CLOSES...).
         Desktop skips this check entirely — losing focus there already implies a
         real interaction, since there's no OS-level control (like a keyboard
         dismiss button) that can blur an input without one. */
      if (!mqDesktop.matches) {
        const tappedElsewhere =
          lastPointerTarget &&
          !searchWrap.contains(lastPointerTarget) &&
          !resultsEl.contains(lastPointerTarget) &&
          !viewAllEl.contains(lastPointerTarget);
        if (!tappedElsewhere) return;
      }

      setTimeout(() => {
        const active = document.activeElement;
        if (
          searchWrap.contains(active) ||
          resultsEl.contains(active) ||
          viewAllEl.contains(active)
        ) return;
        hideResults();
        collapseSearch();
        searchWrap.classList.remove('is-focused');
      }, 0);
    });

    mqDesktop.addEventListener('change', () => {
      collapseSearch();
    });

    /* Resets the field when the panel closes, so reopening starts clean. The
       submitting guard is belt and braces now that navigation is explicit — the
       term has already been read and the URL built by the time this runs. */
    const mo = new MutationObserver(() => {
      if (panel.getAttribute('aria-hidden') === 'true') {
        hideResults();
        collapseSearch();
        if (searchWrap) searchWrap.classList.remove('is-focused');
        if (!submitting) input.value = '';
        lastTerm = '';
      }
    });
    mo.observe(panel, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  function initAll() {
    document.querySelectorAll('[id^="mega-menu-panel-"]').forEach(initPanel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Theme editor re-renders the section — rebind the fresh DOM. */
  document.addEventListener('shopify:section:load', initAll);
})();
